import { habitsApi } from '../api';
import { habitsRepo } from '../db/habits';
import { checkinsRepo } from '../db/checkins';
import { queueRepo } from '../db/queue';
import { db } from '../db';

const MAX_ATTEMPTS = 5;

async function executeHabit(entry) {
  if (entry.op === 'create') {
    // Pass the local record id as a clientId so the server can dedupe. This makes
    // habit creation idempotent: a re-delivered create (crash mid-create, or the
    // anon-create + migration double-enqueue) returns the SAME server habit
    // instead of spawning a duplicate.
    const clientId = entry.payload?.clientId || entry.recordId;
    const created = await habitsApi.create({ ...entry.payload, clientId });
    const realId = created.habit?.habitId || created.habitId;
    if (!realId) throw new Error('Server did not return habitId');
    await habitsRepo.rekey(entry.recordId, realId);
  } else if (entry.op === 'update') {
    if (entry.recordId.startsWith('local-')) {
      throw new Error('waiting for create');
    }
    await habitsApi.update(entry.recordId, entry.payload);
    // Mark synced so subsequent server pulls can overwrite.
    const existing = await db.habits.get(entry.recordId);
    if (existing) {
      await db.habits.put({ ...existing, syncStatus: 'synced' });
    }
  } else if (entry.op === 'delete') {
    if (entry.recordId.startsWith('local-')) {
      await habitsRepo.hardDelete(entry.recordId);
      return;
    }
    await habitsApi.delete(entry.recordId);
    await habitsRepo.hardDelete(entry.recordId);
  }
}

async function executeCheckin(entry) {
  const habitId = entry.payload?.habitId;
  const date = entry.payload?.date;
  if (!habitId || !date) throw new Error('Bad checkin payload');
  if (typeof habitId === 'string' && habitId.startsWith('local-')) {
    throw new Error('waiting for create');
  }
  if (entry.op === 'create') {
    await habitsApi.checkin(habitId, date);
    const id = `${habitId}#${date}`;
    const existing = await db.checkins.get(id);
    if (existing) {
      await db.checkins.put({ ...existing, syncStatus: 'synced' });
    }
  } else if (entry.op === 'delete') {
    await habitsApi.undoCheckin(habitId, date);
    await checkinsRepo.hardDelete(`${habitId}#${date}`);
  }
}

async function execute(entry) {
  switch (entry.entity) {
    case 'habit':
      return executeHabit(entry);
    case 'checkin':
      return executeCheckin(entry);
    default:
      throw new Error(`Unknown entity: ${entry.entity}`);
  }
}

let inFlight = false;

// A permanent client error (bad request / not found / conflict) will never
// succeed on retry, so it should die immediately rather than burn attempts.
// 429 (rate limit) and 5xx / network errors are transient and should retry.
function isPermanent(status) {
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 429;
}

export async function drainQueue() {
  if (inFlight) return { drained: 0, failed: 0 };
  inFlight = true;
  let drained = 0;
  let failed = 0;
  try {
    // Recover any mutations stranded in 'in_flight' by a previous crash/kill.
    await queueRepo.requeueStaleInFlight().catch(() => undefined);
    while (true) {
      const batch = await queueRepo.listPending(20);
      if (batch.length === 0) break;
      let progressed = false;
      for (const entry of batch) {
        if (entry.id === undefined) continue;
        await queueRepo.markInFlight(entry.id);
        try {
          await execute(entry);
          await queueRepo.markSucceeded(entry.id);
          drained++;
          progressed = true;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          if (message === 'waiting for create') {
            // A soft ordering dependency, NOT a real failure — do not count it
            // toward death. Reset to pending and stop this pass; the create
            // ahead (or a future pass) will unblock it.
            await queueRepo.resetToPending(entry.id);
            break;
          }
          const dead = isPermanent(err?.status) || entry.attempts + 1 >= MAX_ATTEMPTS;
          await queueRepo.markFailed(entry.id, message, dead);
          if (dead) {
            console.error('Mutation permanently failed (dropped):', entry.entity, entry.op, message);
          }
          failed++;
        }
      }
      // If nothing succeeded this pass, stop — retrying the same batch
      // immediately won't help (a blocked-on-create entry unblocks on the next
      // drain trigger once its create has landed).
      if (!progressed) break;
    }
  } finally {
    inFlight = false;
  }
  return { drained, failed };
}
